import { Integration } from '@futrli-sdk/app-store';
import { Organisation } from '@futrli-sdk/organisations';
import { Permission, WorkspacesListingItem } from '@futrli-sdk/workspaces';
import {
  getOrganisationById,
  getOrganisations,
  getParamFromRoute,
  isMobileOrTabletWindowSize,
  organisationByIdSelector,
  ROUTE_PARAMS,
  workspaceByIdSelector,
  matchConsolidationProductRoutePath,
  getTransformedOrgData
} from '@futrli/shared-services';
import React from 'react';
import { connect } from 'react-redux';
import { useLocation, useParams, Location } from 'react-router-dom';

import { useIntegrationsView } from '@futrli/views/hooks/useIntegrationsView';

import { StoreState } from '../../core/redux-utils/types';
import { DesktopNavBarScaffold } from '../../app/components/responsive/desktop-navbar-scaffold';

import navbar from '../../app/components/navbar.scss';
import { ProductsNavbarMobile } from '../components/products-navbar-mobile';
import { PredictConsolidationNavbar } from '@futrli/predict/predict-management/components/consolidation/predict-consolidation-navbar';
import { PredictOrganisationNavbar } from '@futrli/predict/predict-management/components/predict-organisation-navbar';

interface RouterProps {
  integrations: Integration[] | null;
  params: Record<string, string | undefined>;
  location: Location;
}

interface StateToProps {
  readonly organisations: Organisation[] | null;
  readonly organisationById: Organisation | null;
  readonly permissions: Permission[] | null;
}

interface DispatchToProps {
  readonly getOrganisations: typeof getOrganisations;
  readonly getOrganisationById: typeof getOrganisationById;
}

interface State {
  isLoadingOrganisations: boolean;
}

type Props = RouterProps & StateToProps & DispatchToProps;

export class ProductsNavBarContainerBase extends React.PureComponent<Props, State> {
  public readonly state: Readonly<State> = {
    isLoadingOrganisations: false
  };

  public componentDidMount(): void {
    const { params, organisationById, location } = this.props;
    const workspaceId: string = getParamFromRoute(params, ROUTE_PARAMS.WORKSPACE_ID) as string;
    const orgId: string = getParamFromRoute(params, ROUTE_PARAMS.ORGANISATION_ID) as string;
    const isConsolidation = matchConsolidationProductRoutePath(location.pathname);

    if (!isConsolidation && (!organisationById || organisationById.id !== orgId)) {
      this.props.getOrganisationById(workspaceId, orgId);
      this.props.getOrganisations(workspaceId);
    }
  }

  public async componentDidUpdate(): Promise<void> {
    const { params, organisations, permissions } = this.props;
    const { isLoadingOrganisations } = this.state;
    const workspaceId: string = getParamFromRoute(params, ROUTE_PARAMS.WORKSPACE_ID) as string;

    // if we are loading organisations, have a workspace id, or have organisations, return
    if (isLoadingOrganisations || !workspaceId || !permissions || organisations) {
      return;
    }

    this.setState({
      isLoadingOrganisations: true
    });

    await this.props.getOrganisations(workspaceId);

    this.setState({
      isLoadingOrganisations: false
    });
  }

  public render() {
    const { organisations, organisationById, permissions, location } = this.props;
    const isConsolidation = matchConsolidationProductRoutePath(location.pathname);

    if (!permissions || (!isConsolidation && (!organisations || !organisationById))) {
      return <React.Fragment />;
    }

    if (isMobileOrTabletWindowSize()) {
      return <ProductsNavbarMobile />;
    }

    return (
      <DesktopNavBarScaffold
        customClassName={navbar.flexOneThird}
        center={isConsolidation ? <PredictConsolidationNavbar /> : <PredictOrganisationNavbar />}
      />
    );
  }
}

const mapStateToProps = (state: StoreState, ownProps: RouterProps): StateToProps => {
  const workspaceId = getParamFromRoute(ownProps.params, ROUTE_PARAMS.WORKSPACE_ID) as string;
  const organisationId = getParamFromRoute(ownProps.params, ROUTE_PARAMS.ORGANISATION_ID) as string;
  const { normalised } = state;

  let storedPermissions: Permission[] | null = [];
  let storedOrganisations: Organisation[] | null = [];
  const storedIntegrations = ownProps.integrations;

  const storedWorkspace = workspaceByIdSelector(normalised, workspaceId) as WorkspacesListingItem;
  storedPermissions = storedWorkspace ? storedWorkspace.permissions : null;
  storedOrganisations = storedWorkspace
    ? storedWorkspace.organisations.map(o => getTransformedOrgData(o, workspaceId, storedIntegrations || []))
    : null;

  const isConsolidationPage = !!matchConsolidationProductRoutePath(ownProps.location.pathname);
  return {
    organisations: storedOrganisations,
    organisationById: isConsolidationPage ? null : organisationByIdSelector(normalised, workspaceId, organisationId),
    permissions: storedPermissions
  };
};

export const ProductsNavBarContainerWS = connect(mapStateToProps, {
  getOrganisations,
  getOrganisationById
})(ProductsNavBarContainerBase);

export const ProductsNavBarContainer: React.FC = () => {
  const params = useParams();
  const location = useLocation();
  const { integrations } = useIntegrationsView();
  return <ProductsNavBarContainerWS integrations={integrations ?? null} params={params} location={location} />;
};
